/**
 * NBM Theme — Main theme JavaScript
 *
 * Handles dark/light mode, toast notifications, cart badge updates,
 * AJAX add-to-cart, and global utilities for the Shopify 2.0 theme.
 *
 * Global namespace: window.NBMTheme
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  Constants                                                          */
  /* ------------------------------------------------------------------ */

  const STORAGE_KEY = 'nbm-theme-mode';
  const VALID_MODES = ['light', 'dark', 'system'];
  const TOAST_CONTAINER_ID = 'nbm-toast-container';

  /* ------------------------------------------------------------------ */
  /*  Dark / Light Mode                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Resolve the effective theme (light | dark) from the stored mode.
   * When mode is 'system', defer to the OS preference.
   */
  function resolveTheme(mode) {
    if (mode === 'dark') return 'dark';
    if (mode === 'light') return 'light';
    // 'system' — check OS preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }

  /** Apply the resolved theme to the <html> element. */
  function applyTheme(mode) {
    const theme = resolveTheme(mode);
    document.documentElement.setAttribute('data-theme', theme);
  }

  /** Read the persisted mode from localStorage, falling back to 'system'. */
  function getStoredMode() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && VALID_MODES.includes(stored)) return stored;
    } catch (error) { /* localStorage may be unavailable */ }
    return 'system';
  }

  /** Persist the chosen mode and apply it immediately. */
  function setMode(mode) {
    if (!VALID_MODES.includes(mode)) {
      console.warn(`[NBMTheme] Invalid mode "${mode}". Use light, dark, or system.`);
      return;
    }
    try { localStorage.setItem(STORAGE_KEY, mode); } catch (error) { /* noop */ }
    applyTheme(mode);
  }

  /** Return the currently stored mode string. */
  function getMode() {
    return getStoredMode();
  }

  /** Cycle through modes: light -> dark -> system -> light ... */
  function toggle() {
    const current = getStoredMode();
    const next = current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
    setMode(next);
  }

  /** Listen for OS-level color-scheme changes when mode is 'system'. */
  function initSystemListener() {
    if (!window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (getStoredMode() === 'system') applyTheme('system');
    };
    mql.addEventListener('change', handler);
  }

  /* ------------------------------------------------------------------ */
  /*  Toast Notifications                                                */
  /* ------------------------------------------------------------------ */

  /** Ensure the fixed-position toast container exists in the DOM. */
  function getToastContainer() {
    let container = document.getElementById(TOAST_CONTAINER_ID);
    if (!container) {
      container = document.createElement('div');
      container.id = TOAST_CONTAINER_ID;
      container.setAttribute('aria-live', 'polite');
      container.setAttribute('role', 'status');
      Object.assign(container.style, {
        position: 'fixed',
        bottom: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: '10000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        pointerEvents: 'none',
        width: '100%',
        maxWidth: '400px',
        padding: '0 16px',
        boxSizing: 'border-box'
      });
      document.body.appendChild(container);
    }
    return container;
  }

  /**
   * Show a toast notification.
   *
   * @param {string} message  — Text to display.
   * @param {string} [type]   — 'success' | 'error' | 'info' (default 'success').
   * @param {number} [duration] — Milliseconds before auto-dismiss (default 3000).
   */
  function toast(message, type, duration) {
    type = type || 'success';
    duration = typeof duration === 'number' ? duration : 3000;

    const container = getToastContainer();
    const el = document.createElement('div');

    // Accent colour per type (used for left-border indicator — matches app icon accent)
    const accentMap = {
      success: 'var(--color-success, #16a34a)',
      error:   'var(--color-error,   #dc2626)',
      warning: 'var(--color-warning, #f59e0b)',
      info:    'var(--color-info,    #2563eb)'
    };
    const accent = accentMap[type] || accentMap.info;

    Object.assign(el.style, {
      background: 'var(--color-text-primary, #1a1a1a)',
      color: 'var(--color-bg-primary, #ffffff)',
      padding: '10px 20px 10px 16px',
      borderRadius: '20px',
      borderLeft: '3px solid ' + accent,
      fontSize: '14px',
      fontWeight: '500',
      lineHeight: '1.4',
      boxShadow: '0 4px 16px rgba(0,0,0,.2)',
      opacity: '0',
      transition: 'opacity .25s ease',
      pointerEvents: 'auto',
      cursor: 'pointer',
      width: 'auto',
      maxWidth: '100%',
      boxSizing: 'border-box',
      textAlign: 'center'
    });

    el.textContent = message;
    container.appendChild(el);

    // Trigger reflow then fade in
    void el.offsetHeight;
    el.style.opacity = '1';

    const dismiss = () => {
      el.style.opacity = '0';
      setTimeout(() => {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, 250);
    };

    el.addEventListener('click', dismiss);
    setTimeout(dismiss, duration);
  }

  /* ------------------------------------------------------------------ */
  /*  Utilities                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Format a price in cents to a currency string (e.g. 1999 → "$19.99").
   */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ------------------------------------------------------------------ */
  /*  Cart Badge                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Fetch the current cart and update every `.cart-badge` element.
   * Shows the total item count, "99+" for counts above 99, hides if 0.
   */
  function updateCartBadge() {
    const cartUrl = (window.NBMRoutes && window.NBMRoutes.cart) || '/cart.js';
    return fetch(cartUrl, {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((res) => {
        if (!res.ok) throw new Error('Cart fetch failed');
        return res.json();
      })
      .then((cart) => {
        const count = cart.item_count || 0;
        const badges = document.querySelectorAll('.cart-badge');
        for (const badge of badges) {
          if (count === 0) {
            badge.textContent = '';
            badge.style.display = 'none';
          } else {
            badge.textContent = count > 99 ? '99+' : String(count);
            badge.style.display = '';
          }
        }
        return count;
      })
      .catch((err) => {
        console.error('[NBMTheme] updateCartBadge error:', err);
      });
  }

  /**
   * Immediately increment the cart badge count without fetching.
   */
  function bumpCartBadge(addedQty) {
    const badges = document.querySelectorAll('.cart-badge');
    for (const badge of badges) {
      const current = parseInt(badge.textContent, 10) || 0;
      const newCount = current + addedQty;
      badge.textContent = newCount > 99 ? '99+' : String(newCount);
      badge.style.display = '';
      badge.removeAttribute('hidden');
      // Quick pop animation
      badge.style.transform = 'scale(1.4)';
      badge.style.transition = 'transform 0.2s ease';
      setTimeout((badgeEl) => {
        badgeEl.style.transform = 'scale(1)';
      }, 200, badge);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  AJAX Add to Cart                                                   */
  /* ------------------------------------------------------------------ */

  let addingToCart = false;

  /**
   * Add a variant to the cart via AJAX.
   *
   * @param {number|string} variantId — Shopify variant ID.
   * @param {number} [quantity]       — Quantity to add (default 1).
   * @returns {Promise<Object>}       — Resolves with the line-item JSON.
   */
  function addToCart(variantId, quantity) {
    if (addingToCart) return Promise.resolve(null);
    variantId = parseInt(variantId, 10);
    quantity = quantity || 1;

    if (!variantId || isNaN(variantId)) {
      return Promise.reject(new Error('Invalid variant ID'));
    }

    addingToCart = true;
    const cartAddUrl = (window.NBMRoutes && window.NBMRoutes.cartAdd) || '/cart/add.js';
    return fetch(cartAddUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: `id=${variantId}&quantity=${quantity}`
    })
      .then((res) => {
        return res.text().then((text) => {
          let data;
          try { data = JSON.parse(text); } catch (error) { data = null; }

          if (!res.ok) {
            const msg = (data && (data.description || data.message)) || `Cart error ${res.status}: ${text.substring(0, 100)}`;
            throw new Error(msg);
          }
          return data;
        });
      })
      .then((item) => {
        if (item && item.title) {
          toast(`${item.title} added to cart`, 'success');
        } else {
          toast('Added to cart', 'success');
        }
        // Update badge immediately without extra fetch
        bumpCartBadge(quantity);
        return item;
      })
      .catch((err) => {
        toast(err.message || 'Error adding to cart', 'error');
        throw err;
      })
      .finally(() => {
        addingToCart = false;
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Event Delegation — Theme Toggle Buttons                            */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('.js-theme-toggle');
    if (btn) {
      event.preventDefault();
      toggle();
      return;
    }

    // Quick add-to-cart (product cards)
    const quickAddBtn = event.target.closest('.js-quick-add');
    if (quickAddBtn) {
      event.preventDefault();
      event.stopPropagation();
      const variantId = quickAddBtn.getAttribute('data-variant-id');
      if (!variantId) return;
      quickAddBtn.disabled = true;
      quickAddBtn.classList.add('is-loading');
      addToCart(variantId, 1)
        .catch(() => { /* toast handled inside addToCart */ })
        .finally(() => {
          quickAddBtn.disabled = false;
          quickAddBtn.classList.remove('is-loading');
        });
    }
  });

  /* ------------------------------------------------------------------ */
  /*  Newsletter Subscribe Form                                          */
  /* ------------------------------------------------------------------ */

  function initSubscribeForm() {
    // Show success toast if Shopify redirected back after a successful subscription.
    if (new URLSearchParams(window.location.search).get('customer_posted') === 'true') {
      history.replaceState(null, '', window.location.pathname + window.location.hash);
      toast('You\'re in! Thanks for subscribing.', 'success');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Init on DOMContentLoaded                                           */
  /* ------------------------------------------------------------------ */

  function init() {
    updateCartBadge();
    initSubscribeForm();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Shared carousel initializer with arrow buttons + page dots.
   *
   * Usage:
   *   NBMTheme.initCarousel({ track, prevBtn, nextBtn, dotsContainer })
   *
   * - track         {Element}  The scrollable .carousel__track element
   * - prevBtn       {Element}  Previous arrow button
   * - nextBtn       {Element}  Next arrow button
   * - dotsContainer {Element}  Element to render dot buttons into (optional)
   */
  function initCarousel(opts) {
    var track        = opts.track;
    var prevBtn      = opts.prevBtn;
    var nextBtn      = opts.nextBtn;
    var dotsEl       = opts.dotsContainer || null;

    var dots      = [];
    var pageCount = 0;

    function getPageCount() {
      var visibleW = track.offsetWidth;
      if (visibleW <= 0) return 1;
      var maxScroll = track.scrollWidth - visibleW;
      if (maxScroll <= 4) return 1;
      return Math.ceil(maxScroll / visibleW) + 1;
    }

    function getCurrentPage() {
      if (pageCount <= 1) return 0;
      var maxScroll = track.scrollWidth - track.offsetWidth;
      if (maxScroll <= 0) return 0;
      return Math.round((track.scrollLeft / maxScroll) * (pageCount - 1));
    }

    function scrollToPage(page) {
      page = Math.max(0, Math.min(pageCount - 1, page));
      if (pageCount <= 1) return;
      var maxScroll = track.scrollWidth - track.offsetWidth;
      track.scrollTo({ left: (page / (pageCount - 1)) * maxScroll, behavior: 'smooth' });
    }

    function updateArrows() {
      if (!prevBtn || !nextBtn) return;
      var firstItem = track.querySelector('.carousel__item');
      var origin = firstItem ? firstItem.offsetLeft : 0;
      prevBtn.disabled = track.scrollLeft <= origin + 4;
      nextBtn.disabled = track.scrollLeft + track.offsetWidth >= track.scrollWidth - origin - 4;
    }

    function updateDots() {
      if (!dotsEl) return;
      var current = getCurrentPage();
      for (var index = 0; index < dots.length; index++) {
        dots[index].classList.toggle('is-active', index === current);
      }
    }

    function buildDots() {
      if (!dotsEl) return;
      dotsEl.innerHTML = '';
      dots = [];
      pageCount = getPageCount();
      if (pageCount <= 1) { dotsEl.hidden = true; return; }
      dotsEl.hidden = false;
      for (var index = 0; index < pageCount; index++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'rv-dot' + (index === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Go to page ' + (index + 1));
        (function (pageIndex) {
          dot.addEventListener('click', function () { scrollToPage(pageIndex); });
        }(index));
        dotsEl.appendChild(dot);
        dots.push(dot);
      }
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { scrollToPage(getCurrentPage() - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { scrollToPage(getCurrentPage() + 1); });

    track.addEventListener('scroll', function () {
      updateDots();
      updateArrows();
    }, { passive: true });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        buildDots();
        updateDots();
        updateArrows();
      }, 150);
    });

    buildDots();
    updateArrows();

    // Return public API so callers can trigger rebuilds (e.g. after dynamic content)
    return { buildDots: buildDots, updateDots: updateDots, updateArrows: updateArrows, scrollToPage: scrollToPage };
  }

  /* ------------------------------------------------------------------ */
  /*  Judge.me rating label                                             */
  /* ------------------------------------------------------------------ */

  function initRatingLabels() {
    document.querySelectorAll('.product-card__rating .jdgm-prev-badge, .product-detail__rating .jdgm-prev-badge').forEach(function (badge) {
      var rating = parseFloat(badge.getAttribute('data-average-rating'));
      var count = parseInt(badge.getAttribute('data-number-of-reviews'), 10);
      var container = badge.parentElement;

      // Remove previously injected label to avoid duplicates on re-runs
      var prev = container.querySelector('.rating-label');
      if (prev) prev.remove();

      if (!rating || count === 0) return;

      var label = document.createElement('span');
      label.className = 'rating-label';
      label.textContent = rating.toFixed(1) + ' / 5.0 (' + count + ' review' + (count === 1 ? '' : 's') + ')';
      badge.insertAdjacentElement('afterend', label);
    });
  }

  document.addEventListener('DOMContentLoaded', initRatingLabels);
  document.addEventListener('jdgm.judgeMe.done', initRatingLabels);

  window.NBMTheme = {
    setMode,
    getMode,
    toggle,
    toast,
    updateCartBadge,
    addToCart,
    initCarousel,
    formatMoney
  };
})();
